import { Form, Select, DatePicker as AntdDatePicker } from 'antd'
import { Moment } from 'moment'
import { FC, useCallback } from 'react'

import { RiArrowDownSLine } from 'react-icons/ri'
import { Rule } from 'antd/lib/form'

import styles from './styles.module.scss'

const { WeekPicker, MonthPicker, RangePicker, QuarterPicker, YearPicker } =
	AntdDatePicker

const { Item } = Form

export type EventValue<DateType> = DateType | null
export type RangeValue<DateType> =
	| [EventValue<DateType>, EventValue<DateType>]
	| null

export type DateType = 'day' | 'week' | 'month' | 'quarter' | 'year' | 'range'

export interface DatePickerValue {
	type: DateType
	value: string | string[]
}

export const DatePickerValidators: Rule[] = [
	{
		validator: (_, value) => {
			if (!value?.value) {
				return Promise.reject(new Error(`Please select ${value?.type || 'date'}`))
			}

			if (value?.type === 'range' && !value?.value) {
				return Promise.reject(new Error('Please select a date range'))
			}

			if (value?.type === 'range' && !Array.isArray(value?.value)) {
				return Promise.reject(new Error('Please select a date range'))
			}

			return Promise.resolve()
		},
		validateTrigger: 'onSubmit',
	},
]

interface DatePickerProps {
	name?: string
	value?: DatePickerValue
	// eslint-disable-next-line no-unused-vars
	onChange?: (_: DatePickerValue) => void
	required?: boolean
	disableDate?: boolean
	format?: string
}

const dateTypeOptions = [
	{
		value: 'day',
		label: 'Day',
	},
	{
		value: 'week',
		label: 'Week',
	},
	{
		value: 'month',
		label: 'Month',
	},
	{
		value: 'quarter',
		label: 'Quarter',
	},
	{
		value: 'year',
		label: 'Year',
	},
	{
		value: 'range',
		label: 'Range',
	},
]

/**
 * @description
 * Dynamic date picker component depending on the date type
 * @author AmirAdal
 * @date 24/05/2023
 */
const DatePicker: FC<DatePickerProps> = ({
	value,
	onChange,
	name,
	required = true,
	disableDate,
	format,
}) => {
	const handleChange = useCallback(
		(type: DateType) => {
			onChange?.({
				type,
				value: '',
			})
		},
		[onChange]
	)

	const handleDateChange = useCallback(
		(
			date: Moment | null | RangeValue<Moment>
			// dateString: string | [string, string],
		) => {
			const newValue = [date].flat().map(d => d?.toJSON() || '')
			onChange?.({
				type: value?.type || 'day',
				value: newValue.length === 1 ? newValue[0] : newValue,
			})
		},
		[onChange, value?.type]
	)
	const handleDisableDate = (current: Moment) => {
		// Can not select Date after today
		return current.valueOf() > Date.now()
	}

	return (
		<div className={styles.datepicker}>
			<Item name={[name || 'date', 'type']} required={required}>
				<Select
					placeholder='Select type'
					options={dateTypeOptions}
					showSearch
					optionFilterProp='label'
					suffixIcon={<RiArrowDownSLine />}
					onChange={handleChange}
				/>
			</Item>
			<Item name={[name || 'date', 'value']} required={required}>
				{(value?.type === 'day' || !value) && (
					<AntdDatePicker
						format={format}
						onChange={handleDateChange}
						disabledDate={disableDate ? handleDisableDate : undefined}
					/>
				)}
				{value?.type === 'week' && (
					<WeekPicker
						onChange={handleDateChange}
						disabledDate={disableDate ? handleDisableDate : undefined}
					/>
				)}
				{value?.type === 'month' && (
					<MonthPicker
						onChange={handleDateChange}
						disabledDate={disableDate ? handleDisableDate : undefined}
					/>
				)}
				{value?.type === 'quarter' && (
					<QuarterPicker
						onChange={handleDateChange}
						disabledDate={disableDate ? handleDisableDate : undefined}
					/>
				)}
				{value?.type === 'year' && (
					<YearPicker
						onChange={handleDateChange}
						disabledDate={disableDate ? handleDisableDate : undefined}
					/>
				)}
				{value?.type === 'range' && (
					<RangePicker
						format={format}
						onChange={handleDateChange}
						disabledDate={disableDate ? handleDisableDate : undefined}
					/>
				)}
			</Item>
		</div>
	)
}

DatePicker.defaultProps = {
	name: undefined,
	onChange: undefined,
	required: undefined,
	value: undefined,
	disableDate: false,
	format: undefined,
}

export default DatePicker
